import { useState, useEffect, useCallback } from 'react';
import { sidoData, sigunguMap, dongMap, RegionNode } from '../data/regions';
import type { SelectedRegion } from '../types';

interface RegionSelectorProps {
  onRegionSelect: (region: SelectedRegion) => void;
  initialRegion?: Partial<SelectedRegion>;
}

export default function RegionSelector({ onRegionSelect, initialRegion }: RegionSelectorProps) {
  const [sido, setSido] = useState<string>(initialRegion?.sido || '');
  const [sigungu, setSigungu] = useState<string>(initialRegion?.sigungu || '');
  const [dong, setDong] = useState<string>(initialRegion?.dong || '');

  const [sigunguList, setSigunguList] = useState<RegionNode[]>([]);
  const [dongList, setDongList] = useState<RegionNode[]>([]);

  // 시/도 선택 시 시/군/구 목록 업데이트
  useEffect(() => {
    if (sido) {
      setSigunguList(sigunguMap[sido] || []);
      setSigungu('');
      setDong('');
      setDongList([]);
    } else {
      setSigunguList([]);
      setSigungu('');
    }
  }, [sido]);

  // 시/군/구 선택 시 읍/면/동 목록 업데이트
  useEffect(() => {
    if (sigungu) {
      setDongList(dongMap[sigungu] || []);
      setDong('');
    } else {
      setDongList([]);
    }
  }, [sigungu]);

  // 지역 선택 완료 시 콜백 호출
  const handleComplete = useCallback(() => {
    if (sido && sigungu && dong) {
      const sidoName = sidoData.find(s => s.code === sido)?.name || '';
      const sigunguName = sigunguList.find(s => s.code === sigungu)?.name || '';
      const dongName = dongList.find(d => d.code === dong)?.name || '';

      onRegionSelect({
        sido,
        sigungu,
        dong,
        sidoName,
        sigunguName,
        dongName,
      });
    }
  }, [sido, sigungu, dong, sigunguList, dongList, onRegionSelect]);

  const isComplete = sido && sigungu && dong;

  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-4">📍 지역 선택</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* 시/도 선택 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            시/도
          </label>
          <select
            className="select"
            value={sido}
            onChange={(e) => setSido(e.target.value)}
          >
            <option value="">선택하세요</option>
            {sidoData.map((item) => (
              <option key={item.code} value={item.code}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        {/* 시/군/구 선택 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            시/군/구
          </label>
          <select
            className="select"
            value={sigungu}
            onChange={(e) => setSigungu(e.target.value)}
            disabled={!sido}
          >
            <option value="">선택하세요</option>
            {sigunguList.map((item) => (
              <option key={item.code} value={item.code}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        {/* 읍/면/동 선택 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            읍/면/동
          </label>
          <select
            className="select"
            value={dong}
            onChange={(e) => setDong(e.target.value)}
            disabled={!sigungu}
          >
            <option value="">선택하세요</option>
            {dongList.map((item) => (
              <option key={item.code} value={item.code}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleComplete}
          disabled={!isComplete}
        >
          데이터 조회
        </button>
      </div>

      {/* 선택한 지역 표시 */}
      {isComplete && (
        <div className="mt-4 p-3 bg-blue-50 rounded-md">
          <p className="text-sm text-blue-700">
            선택한 지역:{' '}
            <span className="font-semibold">
              {sidoData.find(s => s.code === sido)?.name}{' '}
              {sigunguList.find(s => s.code === sigungu)?.name}{' '}
              {dongList.find(d => d.code === dong)?.name}
            </span>
          </p>
        </div>
      )}
    </div>
  );
}